import { ProductPurchaseModel } from 'api-hooks/product/order';
import { generateId } from 'common/utils/string';
import React from 'react';

import {
  ProductPurchaseDetailFormType,
  ProductPurchaseFormType,
  ProductPurchaseOtherFeesFormType,
} from '../components/form-type';

export function addNewPurchase(): ProductPurchaseDetailFormType {
  return {
    id: undefined,
    customId: generateId(),
    description: '',
    price: undefined,
    productVariantId: undefined,
    qty: undefined,
  } as never as ProductPurchaseDetailFormType;
}

export function addNewOtherFees(): ProductPurchaseOtherFeesFormType {
  return {
    id: undefined,
    customId: generateId(),
    name: '',
    description: '',
    price: undefined,
  } as never as ProductPurchaseOtherFeesFormType;
}

export function purchaseToPurchase(
  productPurchase: ProductPurchaseModel,
): ProductPurchaseFormType {
  const agent = productPurchase.agent;
  const paymentChannel = productPurchase.paymentChannel;
  const commission = 0;
  const transactionAt = new Date();
  const productPurchases: ProductPurchaseFormType['productPurchases'] =
    productPurchase.productPurchases.map((pp) => {
      return {
        id: undefined,
        customId: generateId(),
        description: pp.description || '',
        price: pp.price,
        qty: pp.qty,
        productVariantId: {
          id: pp.productVariant.id,
          label: pp.completeName,
        },
      };
    });
  const productPurchaseOtherFees: ProductPurchaseFormType['productPurchaseOtherFees'] =
    productPurchase.productPurchaseOtherFees.map((fee) => {
      return {
        id: undefined,
        customId: generateId(),
        name: fee.name || '',
        description: fee.description || '',
        price: fee.price,
      };
    });

  return {
    agentId: agent.id || '',
    agentStatus: agent.status || '',
    remainingTransactionQuota: null,
    isExceedingQuota: false,
    code: productPurchase.code || '',
    commission,
    total: productPurchase.total - commission,
    description: productPurchase.description || '',
    discount: productPurchase.discount,
    needApproval: false,
    orderNumber: productPurchase.number || '',
    pic: {
      description: agent.description || '',
      email: agent.email || '',
      name: agent.name || '',
      phone: agent.phone || '',
    },
    otherCost: productPurchase.otherCost,
    paymentChannelId: paymentChannel?.id || '',
    paymentChannelName: paymentChannel?.name || '',
    refId: undefined,
    parentRefId: '',
    productPurchase: undefined,
    transactionAt,
    tempTransactionAt: transactionAt,
    productPurchases,
    productPurchaseOtherFees,
  };
}

interface UseGetDefaultValuesProps {
  productPurchase?: ProductPurchaseModel;
  defaultValues?: ProductPurchaseFormType;
}

export default function useGetDefaultValues(
  props: UseGetDefaultValuesProps,
): ProductPurchaseFormType {
  const defaultValues = React.useMemo<ProductPurchaseFormType>(() => {
    if (props.defaultValues) return props.defaultValues;

    const { productPurchase } = props;
    const agent = productPurchase?.agent;
    const paymentChannel = productPurchase?.paymentChannel;
    const transactionAt = productPurchase?.transactionAt ?? new Date();

    const productPurchases: ProductPurchaseFormType['productPurchases'] =
      productPurchase?.productPurchases?.map((pp) => {
        return {
          id: pp.id,
          customId: generateId(),
          description: pp.description || '',
          price: pp.price,
          qty: pp.qty,
          productVariantId: {
            id: pp.productVariant.id,
            label: pp.completeName,
          },
        };
      }) ?? [addNewPurchase()];

    const productPurchaseOtherFees: ProductPurchaseFormType['productPurchaseOtherFees'] =
      productPurchase?.productPurchaseOtherFees.map((fee) => {
        return {
          id: fee.id,
          customId: generateId(),
          name: fee.name || '',
          description: fee.description || '',
          price: fee.price,
        };
      }) ?? [];

    return {
      agentId: agent?.id || '',
      agentStatus: agent?.status || '',
      remainingTransactionQuota: null,
      isExceedingQuota: false,
      code: productPurchase?.code || '',
      commission: productPurchase?.agentCommission?.amount || 0,
      total: productPurchase?.total || 0,
      description: productPurchase?.description || '',
      discount: productPurchase?.discount || 0,
      needApproval: false,
      orderNumber: productPurchase?.number || '',
      pic: {
        description: agent?.description || '',
        email: agent?.email || '',
        name: agent?.name || '',
        phone: agent?.phone || '',
      },
      otherCost: productPurchase?.otherCost || 0,
      paymentChannelId: paymentChannel?.id || '',
      paymentChannelName: paymentChannel?.name || '',
      refId: productPurchase?.refId || undefined,
      parentRefId: productPurchase?.parentPurchase?.id,
      transactionAt,
      tempTransactionAt: transactionAt,
      productPurchases,
      productPurchaseOtherFees,
      productPurchase,
    };
  }, [props]);

  return defaultValues;
}
